import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import {
  useConnection,
  useConnect,
  useConnectors,
  useDisconnect,
  useSwitchChain,
  useWalletClient,
} from 'wagmi';

import {
  IS_METAMASK_IN_APP,
  IS_MOBILE_WEB,
  LOCAL_RPC_URL,
  TARGET,
  TARGET_CHAIN_ID,
} from 'src/config/wagmiConfig';
import { getActiveAddresses } from 'src/config/contract-loader';
import { clearPendingMetaMaskLink } from 'src/utils/metaMaskOpenLink';

const CONTRACT_NAME = import.meta.env.VITE_CONTRACT_NAME || 'FiatCryptoBridge';

function normalizeChainId(id) {
  if (id == null) return null;
  if (typeof id === 'number') return Number.isNaN(id) ? null : id;
  if (typeof id === 'bigint') return Number(id);
  if (typeof id === 'string') {
    const n = id.startsWith('0x') ? Number.parseInt(id, 16) : Number(id);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

function usesLoopbackRpc(url) {
  return typeof url === 'string' && /(127\.0\.0\.1|localhost)/i.test(url);
}

// -------------------------
// Resolve the provider from the active wagmi connector instead of reading
// window.ethereum directly.
//
// Why this matters on MetaMask mobile: the in-app WebView re-injects
// window.ethereum across a background/foreground cycle (and on refresh),
// replacing the global with a *new* provider object. wagmi's connector keeps a
// reference to the ORIGINAL provider — that original is what writeContract /
// eth_sendTransaction actually route through. If we revoke permissions, attach
// listeners, or read permissions on window.ethereum (the new object) while the
// deposit signs through the connector's provider (the old object), we operate
// on the wrong instance: the stale channels/permission that cause the hang live
// on the connector's provider, not on window.ethereum.
//
// connector.getProvider() always returns the instance wagmi will transact
// through, so revoke/listen/inspect stay aligned with the write path. It is
// async, so every caller awaits it. We fall back to window.ethereum only if the
// connector can't supply a provider, so behaviour degrades rather than breaks.
// -------------------------
async function resolveActiveProvider(connector) {
  try {
    if (connector && typeof connector.getProvider === 'function') {
      const provider = await connector.getProvider();
      if (provider) return provider;
    }
  } catch {
    // fall through to window.ethereum
  }

  if (typeof window !== 'undefined' && window.ethereum) {
    return window.ethereum;
  }

  return null;
}

// -------------------------
// Session cleanup helper, now operating on the connector's provider.
// The MetaMask state dump showed 20 stale v2 SDK channel sessions accumulated
// for safiswap.com — one per page load, never pruned. When MetaMask routes an
// RPC response it picks from all live channels; if it picks a dead one the
// writeContract promise on the active page hangs forever. Revoking the
// CAIP-25 permission on disconnect forces MetaMask to tear down all channels
// and start clean on the next connection. Critically, this now revokes on the
// SAME provider the deposit signs through (see resolveActiveProvider), so on
// the mobile re-injection case it actually clears the live session rather than
// a detached window.ethereum instance.
// -------------------------
async function revokeMetaMaskPermissions(connector) {
  try {
    const provider = await resolveActiveProvider(connector);
    if (!provider?.request) return;

    await provider.request({
      method: 'wallet_revokePermissions',
      params: [{ eth_accounts: {} }],
    });
  } catch {
    // Revocation is best-effort — some MetaMask versions do not expose this
    // method and will throw. We intentionally swallow the error so this never
    // blocks the disconnect flow.
  }
}

// -------------------------
// Read the CAIP-25 *authorized* accounts via wallet_getPermissions, not
// eth_accounts.
//
// The previous implementation called eth_accounts, which returns the currently
// SELECTED account — the same value as connection.address by construction — so
// the mismatch comparison could never be true and the feature was dead code.
//
// The actual account-mismatch bug from the state dump (permission granted for
// 0x3e4927 while the active account is 0x03f634) lives in the permission grant,
// which is exposed through wallet_getPermissions. Each permission has a list of
// caveats; the eth_accounts permission carries a caveat (type 'restrictReturnedAccounts'
// in MetaMask, though the exact type string has varied across versions) whose
// value is the array of authorized addresses. We read that array.
//
// Parsing is defensive because the caveat shape differs slightly by MetaMask
// version: we accept any caveat whose value is an array of address-like strings,
// rather than hard-coding a single caveat type. If nothing parses we return an
// empty array, which the caller treats as "can't determine" (no false positive).
// -------------------------
function extractAuthorizedAccountsFromPermissions(permissions) {
  if (!Array.isArray(permissions)) return [];

  const ethAccountsPermission = permissions.find(
    (perm) => perm?.parentCapability === 'eth_accounts'
  );

  if (!ethAccountsPermission || !Array.isArray(ethAccountsPermission.caveats)) {
    return [];
  }

  const permittedCaveat = ethAccountsPermission.caveats.find((caveat) => {
    const value = caveat?.value;
    return Array.isArray(value) && value.every((v) => typeof v === 'string');
  });

  const permittedAccounts = permittedCaveat?.value?.map((address) => address.toLowerCase());

  if (!permittedAccounts) {
    return [];
  }

  return permittedAccounts;
}

const PENDING_REQUEST_CODE = -32002;
// Load the MetaMask connector shortly after the page opens on a phone browser,
// so pressing Connect does not first have to download it.
const MOBILE_PRELOAD_DELAY_MS = 1_500;
const PENDING_APPROVAL_WAIT_MS = 30_000;
const PENDING_APPROVAL_POLL_MS = 1_000;

function getErrorCode(error) {
  return error?.code ?? error?.cause?.code;
}

// eth_accounts never opens a prompt: it returns the accounts this site is
// already allowed to see (empty when not connected yet).
async function readPermittedAccounts(connector) {
  try {
    const provider = await resolveActiveProvider(connector);
    if (!provider?.request) return [];

    const accounts = await provider.request({ method: 'eth_accounts' });
    if (Array.isArray(accounts)) return accounts;
    return [];
  } catch {
    return [];
  }
}

// A connect request is already open in MetaMask (often one the app never
// displayed). Wait for the user to approve it there instead of failing.
async function waitForPermittedAccounts(connector, timeoutMs) {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    const accounts = await readPermittedAccounts(connector);
    if (accounts.length > 0) return accounts;

    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, PENDING_APPROVAL_POLL_MS);
    });
  }

  return [];
}

async function getAuthorizedAccounts(connector) {
  try {
    const provider = await resolveActiveProvider(connector);
    if (!provider?.request) return [];

    const permissions = await provider.request({ method: 'wallet_getPermissions' });
    return extractAuthorizedAccountsFromPermissions(permissions);
  } catch {
    return [];
  }
}

export function useEthereum() {
  const connection = useConnection();
  const connectors = useConnectors();

  const connect = useConnect();
  const disconnect = useDisconnect();
  const switchChain = useSwitchChain();

  const connected = connection.status === 'connected';
  const activeConnector = connection.connector;

  const { data: walletClient } = useWalletClient({
    connector: connection.connector,
    query: {
      enabled: connected,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  });

  const walletChainId = useMemo(() => normalizeChainId(connection.chainId), [connection.chainId]);

  const bridgeAddress = useMemo(() => {
    if (!walletChainId) return null;

    const addresses = getActiveAddresses(walletChainId);

    return addresses?.[CONTRACT_NAME] ?? addresses?.FiatCryptoBridge ?? null;
  }, [walletChainId]);

  const metaMaskConnector = useMemo(() => {
    // injected({ target: 'metaMask' }) has id 'metaMask' and type 'injected'
    // (the old id === 'injected' check never matched it).
    const injectedMetaMask = connectors.find((connector) => {
      return connector.type === 'injected' && /meta.?mask/i.test(connector.name);
    });

    const metaMaskSdk = connectors.find((connector) => {
      return /meta.?mask/i.test(connector.name);
    });

    return injectedMetaMask || metaMaskSdk || null;
  }, [connectors]);

  const statusRef = useRef(connection.status);
  useEffect(() => {
    statusRef.current = connection.status;
  }, [connection.status]);

  // Phone browsers (Chrome, Safari, ...): load the connector early, and pick up
  // a connection that was approved in MetaMask while this tab was reloaded.
  // Android often reloads the browser tab while the user is in MetaMask; the
  // approval is saved by the connector, but wagmi does not know about it yet,
  // so the page showed "Connect" again even though MetaMask had approved.
  useEffect(() => {
    if (!IS_MOBILE_WEB || !metaMaskConnector) return () => {};

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        await metaMaskConnector.getProvider();

        if (cancelled || statusRef.current !== 'disconnected') return;

        const authorized = await metaMaskConnector.isAuthorized();

        if (cancelled || !authorized || statusRef.current !== 'disconnected') return;

        // isReconnecting: uses the saved approval, never opens MetaMask.
        await connect.mutateAsync({ connector: metaMaskConnector, isReconnecting: true });
      } catch {
        // Preload is best effort; Connect still works without it.
      }
    }, MOBILE_PRELOAD_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // connect is stable enough here; re-running on every render would re-preload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaMaskConnector]);

  // Track the account mismatch state. When MetaMask's CAIP-25 permission is held
  // for a different account than the currently-selected one (as found in the
  // state dump), writes silently hang rather than rejecting cleanly. Using
  // useState (not useRef) makes this reactive so consumers re-render when the
  // mismatch is detected and can disable the deposit button or show a reconnect
  // warning before the user reaches the MetaMask popup path.
  const [accountMismatch, setAccountMismatch] = useState(false);

  // -------------------------
  // CHANGE (listener leak fix): the provider we attach the accountsChanged
  // listener to is now held in a ref, not just a closure local. The effect
  // depends on activeConnector, whose identity changes on many wagmi internal
  // updates; each re-run resolves the provider ASYNCHRONOUSLY. With the old
  // closure-local `provider`, if the effect re-fired before the prior run's
  // `await resolveActiveProvider` resolved, that run's cleanup saw `provider`
  // still null and never detached the listener it was about to attach a tick
  // later — the MaxListenersExceededWarning path. Holding the attached provider
  // in a ref lets cleanup always find and detach exactly what was attached,
  // regardless of async timing, and lets us avoid double-attaching to the same
  // instance.
  // -------------------------
  const attachedProviderRef = useRef(null);
  const attachedHandlerRef = useRef(null);

  useEffect(() => {
    // No connector yet, or not connected: clear state and ensure any previously
    // attached listener is torn down. (Returns a cleanup for consistent-return;
    // the detach below is the real work.)
    if (!connected || !connection.address || !activeConnector) {
      setAccountMismatch(false);

      if (attachedProviderRef.current && attachedHandlerRef.current) {
        try {
          attachedProviderRef.current.removeListener?.(
            'accountsChanged',
            attachedHandlerRef.current
          );
        } catch {
          // best-effort detach
        }
      }
      attachedProviderRef.current = null;
      attachedHandlerRef.current = null;

      return () => {};
    }

    let cancelled = false;

    const checkAccountAlignment = async () => {
      const authorized = await getAuthorizedAccounts(activeConnector);

      if (cancelled) return;

      const currentAddress = connection.address?.toLowerCase();
      setAccountMismatch(
        authorized.length > 0 && currentAddress ? !authorized.includes(currentAddress) : false
      );
    };

    const handleAccountsChanged = () => {
      if (!cancelled) checkAccountAlignment();
    };

    // Resolve the connector's provider once, attach the listener to THAT
    // provider (not window.ethereum), and run a single initial check. Attaching
    // to the connector's provider means we hear accountsChanged from the same
    // instance the deposit signs through — on the mobile re-injection case, a
    // listener on window.ethereum would be bound to a detached object and never
    // fire.
    const setup = async () => {
      // Single initial check (previously this ran here AND as the first line of
      // the effect — a redundant double round-trip on every re-run).
      await checkAccountAlignment();

      if (cancelled) return;

      const provider = await resolveActiveProvider(activeConnector);

      if (cancelled || !provider?.on) return;

      // If we somehow still hold a listener on a previous provider, detach it
      // before attaching the new one (defends against any missed cleanup).
      if (
        attachedProviderRef.current &&
        attachedHandlerRef.current &&
        attachedProviderRef.current !== provider
      ) {
        try {
          attachedProviderRef.current.removeListener?.(
            'accountsChanged',
            attachedHandlerRef.current
          );
        } catch {
          // best-effort detach
        }
      }

      // Avoid double-attaching to the same provider instance.
      if (
        attachedProviderRef.current === provider &&
        attachedHandlerRef.current === handleAccountsChanged
      ) {
        return;
      }

      provider.on('accountsChanged', handleAccountsChanged);
      attachedProviderRef.current = provider;
      attachedHandlerRef.current = handleAccountsChanged;
    };

    setup();

    return () => {
      cancelled = true;

      // Detach through the ref so async timing can't strand the listener.
      if (attachedProviderRef.current && attachedHandlerRef.current) {
        try {
          attachedProviderRef.current.removeListener?.(
            'accountsChanged',
            attachedHandlerRef.current
          );
        } catch {
          // best-effort detach
        }
      }
      attachedProviderRef.current = null;
      attachedHandlerRef.current = null;
    };
  }, [connected, connection.address, activeConnector]);

  const isReconnecting = connection.status === 'reconnecting';
  const isBusy =
    isReconnecting || connect.isPending || disconnect.isPending || switchChain.isPending;

  const targetChainId = normalizeChainId(TARGET_CHAIN_ID);

  const walletReady =
    connected && !isReconnecting && walletChainId === targetChainId && Boolean(connection.address);

  const walletClientReady = Boolean(walletClient?.writeContract);

  const switchNetworkError =
    connected &&
    walletChainId != null &&
    targetChainId != null &&
    Number(walletChainId) !== Number(targetChainId);

  const connectWallet = useCallback(async () => {
    if (!metaMaskConnector) {
      throw new Error('MetaMask connector is not available');
    }

    if (connected || connect.isPending) {
      return null;
    }

    if (isReconnecting) {
      throw new Error('Wallet is reconnecting. Please wait.');
    }

    if (isMobileBrowser() && TARGET === 'localhost' && usesLoopbackRpc(LOCAL_RPC_URL)) {
      throw new Error(
        'MetaMask mobile cannot connect to localhost/127.0.0.1. Use your computer LAN IP for Hardhat, e.g. http://192.168.1.x:8545'
      );
    }

    clearPendingMetaMaskLink();

    // Inside the MetaMask app: if this site is already allowed, connect
    // silently (no prompt, so nothing can be left pending).
    if (IS_METAMASK_IN_APP) {
      const permitted = await readPermittedAccounts(metaMaskConnector);

      if (permitted.length > 0) {
        const result = await connect.mutateAsync({
          connector: metaMaskConnector,
          isReconnecting: true,
        });

        setAccountMismatch(false);
        return result;
      }
    }

    try {
      const result = await connect.mutateAsync({
        connector: metaMaskConnector,
      });

      // Reset mismatch flag after a successful fresh connect.
      setAccountMismatch(false);
      clearPendingMetaMaskLink();

      return result;
    } catch (error) {
      const message = String(error?.message || '');
      const code = getErrorCode(error);

      if (code === PENDING_REQUEST_CODE && IS_METAMASK_IN_APP) {
        const approved = await waitForPermittedAccounts(
          metaMaskConnector,
          PENDING_APPROVAL_WAIT_MS
        );

        if (approved.length > 0) {
          connect.reset();

          const result = await connect.mutateAsync({
            connector: metaMaskConnector,
            isReconnecting: true,
          });

          setAccountMismatch(false);
          return result;
        }

        throw new Error(
          'MetaMask still has an earlier connection request open. Pull down to refresh this page, then press Connect again.'
        );
      }

      if (code === PENDING_REQUEST_CODE) {
        throw new Error(
          'A MetaMask request is already open. Open MetaMask and finish or cancel the pending request.'
        );
      }

      if (isMobileBrowser() && /provider/i.test(message) && /not found/i.test(message)) {
        throw new Error(
          'MetaMask provider was not found in this mobile browser. Open this site inside the MetaMask app browser, or continue with the MetaMask mobile deeplink prompt if shown.'
        );
      }

      throw error;
    }
  }, [metaMaskConnector, connected, isReconnecting, connect]);

  // Hard reset path: revoke MetaMask's session, then reconnect. Use ONLY for
  // intentional resets — account mismatch, repeated write timeouts, or an
  // explicit "reset wallet connection" action. NOT for normal connect, because
  // revoking on every connect churns MetaMask's provider stream and leaks
  // close/end listeners (MaxListenersExceededWarning).
  const reconnectWalletFresh = useCallback(async () => {
    if (!metaMaskConnector) {
      throw new Error('MetaMask connector is not available');
    }

    if (connect.isPending || disconnect.isPending) {
      throw new Error('Wallet action already in progress.');
    }

    // Capture the provider currently in use BEFORE disconnecting, so the revoke
    // targets the live session rather than whatever window.ethereum points at
    // after teardown.
    const providerConnector = activeConnector || metaMaskConnector;

    connect.reset();
    disconnect.reset();
    switchChain.reset();

    try {
      await disconnect.mutateAsync({});
    } catch {
      // best-effort disconnect before the revoke
    }

    await revokeMetaMaskPermissions(providerConnector);

    const result = await connect.mutateAsync({
      connector: metaMaskConnector,
    });

    setAccountMismatch(false);
    return result;
  }, [metaMaskConnector, activeConnector, connect, disconnect, switchChain]);

  // Clears a connect attempt that MetaMask never answered. Without this a
  // timed-out attempt stays "pending" and every later Connect does nothing.
  const resetConnect = useCallback(() => {
    connect.reset();
  }, [connect]);

  // Stops a connect the user gave up on (Cancel, or the timeout).
  //
  // It no longer calls the connector's disconnect(). On a phone browser that
  // tried to resume the relay session first ("Resume timeout") and tore down
  // the connector's client while the connect was still running ("DappClient
  // not initialized"), which broke the next Connect as well. Resetting wagmi's
  // pending state is enough; the connector replaces the old request itself.
  const cancelConnect = useCallback(async () => {
    connect.reset();
    clearPendingMetaMaskLink();
  }, [connect]);

  const switchToTargetNetwork = useCallback(async () => {
    if (!connected) throw new Error('Connect MetaMask first.');
    if (isReconnecting) throw new Error('Wallet is reconnecting. Please wait.');

    if (walletChainId === targetChainId) return null;

    return switchChain.mutateAsync({ chainId: targetChainId });
  }, [connected, isReconnecting, walletChainId, targetChainId, switchChain]);

  // Added permission revocation to the disconnect flow, targeting the
  // connector's provider. Previously, disconnectWallet only called wagmi's
  // disconnect which removes local wagmi state but leaves the MetaMask CAIP-25
  // session alive, contributing to channel accumulation. Revoking on the same
  // provider the session lives on ensures MetaMask tears it down on its side too,
  // so reconnecting starts with a single fresh channel.
  // Added permission revocation to the disconnect flow, targeting the
  // connector's provider. wagmi's disconnect removes local state but leaves the
  // MetaMask CAIP-25 session alive, contributing to channel accumulation.
  //
  // TRADE-OFF: revoking forces a full account re-approval on the next connect.
  // That is a real UX tax on a routine disconnect, so it is now a parameter.
  // Default stays `true` to preserve current behaviour — flip the call site to
  // `disconnectWallet({ revoke: false })` once you have confirmed channels no
  // longer accumulate without it.
  const disconnectWallet = useCallback(
    async ({ revoke = true } = {}) => {
      // Capture before teardown so the revoke hits the live provider instance.
      const providerConnector = activeConnector;

      connect.reset();
      disconnect.reset();
      switchChain.reset();
      await disconnect.mutateAsync({});

      // Revoke AFTER wagmi writes its shimDisconnect flag to localStorage.
      // This clears MetaMask channels on intentional disconnect only,
      // and does NOT run on refresh — so reconnectOnMount can still work.
      if (revoke) {
        await revokeMetaMaskPermissions(providerConnector);
      }
    },
    [activeConnector, connect, disconnect, switchChain]
  );

  return useMemo(
    () => ({
      connected,
      status: connection.status,
      isBusy,
      address: connection.address,

      walletClient,
      walletClientReady,

      walletChainId,
      targetChainId,
      switchNetworkError,
      bridgeAddress,
      isReconnecting,
      walletReady,

      isConnecting: connect.isPending,
      isDisconnecting: disconnect.isPending,
      isSwitchingNetwork: switchChain.isPending,

      connectError: connect.error,
      disconnectError: disconnect.error,
      switchChainError: switchChain.error,

      connectWallet,
      resetConnect,
      cancelConnect,
      reconnectWalletFresh,
      switchToTargetNetwork,
      disconnectWallet,

      isMetaMaskInApp: IS_METAMASK_IN_APP,
      isMobileWeb: IS_MOBILE_WEB,

      activeConnectorName: connection.connector?.name ?? null,
      activeConnectorId: connection.connector?.id ?? null,
      hasMetaMaskConnector: Boolean(metaMaskConnector),

      // Expose account mismatch flag for the UI layer. When this is true the
      // selected MetaMask account is NOT among the CAIP-25 authorized accounts,
      // and any deposit attempt will silently fail or hang. The UI should
      // display a "please reconnect" warning and disable the deposit button
      // rather than letting the user reach the MetaMask popup path.
      accountMismatch,
    }),
    [
      connected,
      connection.status,
      connection.address,
      connection.connector?.name,
      connection.connector?.id,
      isBusy,
      walletClient,
      walletClientReady,
      walletChainId,
      targetChainId,
      switchNetworkError,
      bridgeAddress,
      isReconnecting,
      walletReady,
      connect.isPending,
      disconnect.isPending,
      switchChain.isPending,
      connect.error,
      disconnect.error,
      switchChain.error,
      connectWallet,
      resetConnect,
      cancelConnect,
      reconnectWalletFresh,
      switchToTargetNetwork,
      disconnectWallet,
      metaMaskConnector,
      accountMismatch,
    ]
  );
}
